// The left panel: search, chips, facets, the count line, the list, the empty
// state and the place card. It renders from state; it never owns state.

import { markSvg } from './marks.js';
import { activeFilters, clearedFilters } from './state.js';
import { parsePlace, facetCounts, relaxations, kindWord } from './filters.js';
import { nextMass, describeNext, formatMinutes, openNow } from './schedule.js';
import { haversineMiles, formatMiles } from './geo.js';
import { LANGUAGE_NAMES } from './i18n.js';

export const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
const PAGE = 40;

export function createPanel(ctx) {
  const { t, lang, data, store, index } = ctx;
  const $ = (id) => document.getElementById(id);
  const els = { q: $('atlasQ'), results: $('atlasResults'), chips: $('atlasChips'), facets: $('atlasFacets'), context: $('atlasContext'), list: $('atlasList'), more: $('atlasMore'), count: $('atlasCount') };
  const langIndex = data.tenant.languageIndex || null;
  let shown = PAGE;
  let lastMatched = [];

  const langName = (code) => t('lang.' + code, LANGUAGE_NAMES[code] || code);
  const chipLabel = (key, v) => {
    if (key === 'place') { const p = parsePlace(v); return p ? (p.type === 'nb' ? nbName(p.value) : p.value) : v; }
    if (key === 'near') return t('chip.near', { v: formatMiles(v[2], lang) });
    if (key === 'lang') return langName(v);
    if (key === 'day') return t('day.' + v);
    if (key === 'after') return t('chip.after', { v: formatMinutes(v, lang) });
    if (key === 'kind') return t('kind.' + v);
    if (key === 'q') return t('chip.q', { v });
    return t('chip.' + key);
  };
  const nbName = (slug) => { const p = (data.places || []).find((x) => x.s === slug); return p ? p.n : slug; };
  const removePatch = (key) => key === 'near' ? { near: null } : key === 'day' || key === 'after' ? { [key]: -1 } : key === 'q' || key === 'place' || key === 'lang' || key === 'kind' ? { [key]: '' } : { [key]: false };

  function renderChips(s) {
    const items = activeFilters(s);
    els.chips.hidden = !items.length;
    els.chips.innerHTML = items.map(([k, v]) => `<button type="button" class="atlas-chip" data-chip="${k}" aria-label="${esc(t('empty.remove', { what: chipLabel(k, v) }))}"><span>${esc(chipLabel(k, v))}</span><i aria-hidden="true">×</i></button>`).join('')
      + (items.length > 1 ? `<button type="button" class="atlas-chip atlas-chip-clear" data-chip="*">${esc(t('results.clear'))}</button>` : '');
  }
  els.chips.addEventListener('click', (e) => {
    const b = e.target.closest('[data-chip]'); if (!b) return;
    const k = b.dataset.chip;
    store.set(k === '*' ? clearedFilters() : removePatch(k));
  });

  function renderFacets(s, all) {
    const fc = facetCounts(all, langIndex);
    const sched = !!data.tenant.schedules;
    const toggle = (key, label, n) => `<button type="button" class="atlas-facet${s[key] ? ' is-on' : ''}" data-facet="${key}" aria-pressed="${s[key] ? 'true' : 'false'}"${n ? '' : ' disabled'}>${esc(label)}<b>${n}</b></button>`;
    const langOpts = fc.langs.map(([code, n]) => `<option value="${code}"${s.lang === code ? ' selected' : ''}>${esc(langName(code))} (${n})</option>`).join('');
    const kinds = ['c', 'b', 's', 'm'].filter((k) => fc.kinds[k]).map((k) => `<option value="${k}"${s.kind === k ? ' selected' : ''}>${esc(t('kind.' + k))} (${fc.kinds[k]})</option>`).join('');
    const hours = [360, 480, 600, 720, 900, 1020, 1140].map((m) => `<option value="${m}"${s.after === m ? ' selected' : ''}>${formatMinutes(m, lang)}</option>`).join('');
    const days = [0, 1, 2, 3, 4, 5, 6].map((d) => `<option value="${d}"${s.day === d ? ' selected' : ''}>${esc(t('day.' + d))}</option>`).join('');
    const radii = [3, 5, 10, 25].map((r) => `<option value="${r}"${s.near && s.near[2] === r ? ' selected' : ''}>${formatMiles(r, lang)}</option>`).join('');
    els.facets.innerHTML = `
      <div class="atlas-facet-row">
        ${toggle('hc', t('facet.hc'), fc.hc)}${toggle('ha', t('facet.ha'), fc.ha)}${toggle('hp', t('facet.hp'), fc.hp)}${toggle('lm', t('facet.lm'), fc.lm)}${toggle('sc', t('facet.sc'), fc.sc)}
        ${sched ? toggle('now', t('facet.now'), all.length) : ''}
      </div>
      <div class="atlas-facet-row atlas-facet-selects">
        <label><span>${esc(t('facet.language'))}</span><select data-select="lang"><option value="">${esc(t('facet.any'))}</option>${langOpts}</select></label>
        <label><span>${esc(t('facet.kind'))}</span><select data-select="kind"><option value="">${esc(t('facet.any'))}</option>${kinds}</select></label>
        ${sched ? `<label><span>${esc(t('facet.day'))}</span><select data-select="day"><option value="-1">${esc(t('facet.anyDay'))}</option>${days}</select></label>
        <label><span>${esc(t('facet.after'))}</span><select data-select="after"><option value="-1">${esc(t('facet.anyHour'))}</option>${hours}</select></label>` : ''}
        <label class="atlas-facet-near"><span>${esc(t('near.me'))}</span><span class="atlas-near-controls"><button type="button" class="atlas-btn" data-locate>${esc(s.near ? t('near.within', { n: '' }).trim() : t('near.me'))}</button><select data-select="radius"${s.near ? '' : ' disabled'}>${radii}</select></span></label>
      </div>`;
  }
  els.facets.addEventListener('click', (e) => {
    const f = e.target.closest('[data-facet]');
    if (f) { const k = f.dataset.facet; store.set({ [k]: !store.get()[k] }); return; }
    if (e.target.closest('[data-locate]')) ctx.onLocate();
  });
  els.facets.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-select]'); if (!sel) return;
    const k = sel.dataset.select, v = sel.value;
    if (k === 'day' || k === 'after') store.set({ [k]: Number(v) });
    else if (k === 'radius') { const s = store.get(); if (s.near) store.set({ near: [s.near[0], s.near[1], Number(v)] }); }
    else store.set({ [k]: v });
  });

  function cardHtml(c, origin) {
    const clock = ctx.clock(c);
    const next = data.tenant.schedules ? nextMass(c.ms, clock, langIndex) : null;
    const dist = origin ? formatMiles(haversineMiles(origin[0], origin[1], c.lat, c.lng), lang) : '';
    const tags = [];
    if (c.hc) tags.push(openNow(c.cs, clock) ? t('drawer.confessionNow') : t('facet.hc'));
    if (c.ha) tags.push(c.hp ? t('facet.hp') : openNow(c.as, clock) ? t('drawer.adorationNow') : t('facet.ha'));
    if (c.lm) tags.push(t('facet.lm'));
    const langs = (c.l || []).filter((x) => x !== 'eng').slice(0, 3).map(langName);
    return `<li><button type="button" class="atlas-card" data-id="${esc(c.i)}" aria-label="${esc(c.n)}">
      <span class="atlas-card-mark">${markSvg(c.k, 16)}</span>
      <span class="atlas-card-body">
        <span class="atlas-card-name">${esc(c.n)}</span>
        <span class="atlas-card-meta">${esc([c.k !== 'p' ? t('kind.' + c.k) : '', c.c, dist].filter(Boolean).join(' · '))}</span>
        ${next ? `<span class="atlas-card-next"><b>${esc(t('drawer.next'))}</b> ${esc(describeNext(next, lang, [0, 1, 2, 3, 4, 5, 6].map((d) => t('day.' + d))))}${next.lang && next.lang !== 'eng' ? ` · ${esc(langName(next.lang))}` : ''}</span>` : ''}
        ${tags.length || langs.length ? `<span class="atlas-card-tags">${[...langs, ...tags].map((x) => `<i>${esc(x)}</i>`).join('')}</span>` : ''}
      </span></button></li>`;
  }

  function renderList(matched, origin) {
    lastMatched = matched;
    const slice = matched.slice(0, shown);
    els.list.innerHTML = slice.map((c) => cardHtml(c, origin)).join('');
    els.more.hidden = matched.length <= shown;
    els.more.textContent = t('results.more') + ` (${matched.length - shown})`;
    const sel = store.get().sel;
    if (sel) highlight(sel);
  }
  els.more.addEventListener('click', () => { shown += PAGE; renderList(lastMatched, ctx.origin()); });
  els.list.addEventListener('click', (e) => { const b = e.target.closest('[data-id]'); if (b) ctx.onSelect(b.dataset.id, 'list'); });
  els.list.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const buttons = [...els.list.querySelectorAll('.atlas-card')];
    const i = buttons.indexOf(document.activeElement); if (i < 0) return;
    e.preventDefault();
    const n = buttons[Math.max(0, Math.min(buttons.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (n) n.focus();
  });
  function highlight(id) {
    for (const b of els.list.querySelectorAll('.atlas-card')) b.classList.toggle('is-selected', b.dataset.id === id);
    // Scroll the list itself, never the page: scrollIntoView would also nudge
    // the overflow-hidden map frame and leave the controls a few pixels off.
    const hit = els.list.querySelector(`.atlas-card[data-id="${CSS.escape(id)}"]`);
    if (!hit) return;
    const li = hit.parentElement;
    const horizontal = els.list.scrollWidth > els.list.clientWidth + 8;
    if (horizontal) els.list.scrollTo({ left: li.offsetLeft - 12, behavior: 'smooth' });
    else if (li.offsetTop < els.list.scrollTop || li.offsetTop + li.offsetHeight > els.list.scrollTop + els.list.clientHeight) els.list.scrollTo({ top: li.offsetTop - 8, behavior: 'smooth' });
  }

  function renderContext(s, matched, all, origin) {
    const n = matched.length, total = all.length;
    const filters = activeFilters(s);
    let html = '';
    if (n === 0 && filters.length) {
      const rel = relaxations(all, s, ctx.filterCtx());
      html += `<div class="atlas-empty"><p>${esc(filters.length > 1 ? t('empty.title', { n: filters.length }) : t('empty.one'))}</p>`;
      if (rel.length) html += `<ul>${rel.map((r) => r.key === 'radius'
        ? `<li><button type="button" class="atlas-link" data-relax="radius" data-miles="${r.miles}">${esc(t('empty.widen', { miles: formatMiles(r.miles, lang) }))}</button><span>${esc(t('empty.count', { n: r.count }))}</span></li>`
        : `<li><button type="button" class="atlas-link" data-relax="${r.key}">${esc(t('empty.remove', { what: chipLabel(r.key, s[r.key]) }))}</button><span>${esc(t('empty.count', { n: r.count }))}</span></li>`).join('')}</ul>`;
      else html += `<p class="atlas-muted">${esc(t('empty.none'))}</p>`;
      html += '</div>';
    } else {
      const sorted = origin ? t('results.sorted.distance') : t('results.sorted.name');
      html += `<div class="atlas-countline"><span>${esc(n === 1 ? t('results.one') : t('results.count', { n }))}${n !== total ? ` <em>${esc(t('results.of', { total }))}</em>` : ''} <small>· ${esc(sorted)}</small></span><button type="button" class="atlas-link" data-fit>${esc(t('results.fit'))}</button></div>`;
      const p = parsePlace(s.place);
      if (p && n) html += placeCard(p, matched, all);
    }
    els.context.innerHTML = html;
  }
  els.context.addEventListener('click', (e) => {
    const r = e.target.closest('[data-relax]');
    if (r) { const s = store.get(); if (r.dataset.relax === 'radius') store.set({ near: [s.near[0], s.near[1], Number(r.dataset.miles)] }); else store.set(removePatch(r.dataset.relax)); return; }
    if (e.target.closest('[data-fit]')) ctx.onFit();
  });

  function placeCard(p, matched, all) {
    const fc = facetCounts(matched, langIndex);
    const name = p.type === 'nb' ? nbName(p.value) : p.value;
    const tenantLangs = (data.tenant.langs || []).map((x) => x.toLowerCase());
    const present = new Set(fc.langs.map(([code]) => LANGUAGE_NAMES[code] ? LANGUAGE_NAMES[code].toLowerCase() : code));
    const gaps = tenantLangs.filter((x) => !present.has(x)).slice(0, 4);
    const notable = matched.filter((c) => c.k !== 'p').slice(0, 4);
    const strengths = [];
    if (fc.hc / matched.length >= 0.6) strengths.push(t('facet.hc'));
    if (fc.ha / matched.length >= 0.4) strengths.push(t('facet.ha'));
    if (fc.hp) strengths.push(t('facet.hp'));
    if (fc.lm) strengths.push(t('facet.lm'));
    const sacGaps = [['hc', t('facet.hc')], ['ha', t('facet.ha')]].filter(([k]) => !fc[k]).map(([, l]) => l);
    return `<section class="atlas-place" aria-label="${esc(name)}">
      <h2>${esc(name)} <small>${esc(t('region.count', { n: matched.length }))}</small></h2>
      <dl>
        <dt>${esc(t('region.languages'))}</dt><dd>${fc.langs.slice(0, 5).map(([code, n]) => `<i>${esc(langName(code))} <b>${n}</b></i>`).join('') || '<i>' + esc(t('region.gaps')) + '</i>'}</dd>
        <dt>${esc(t('region.sacraments'))}</dt><dd><i>${esc(t('facet.hc'))} <b>${fc.hc}</b></i><i>${esc(t('facet.ha'))} <b>${fc.ha}</b></i>${fc.hp ? `<i>${esc(t('facet.hp'))} <b>${fc.hp}</b></i>` : ''}${fc.lm ? `<i>${esc(t('facet.lm'))} <b>${fc.lm}</b></i>` : ''}</dd>
        ${notable.length ? `<dt>${esc(t('region.notable'))}</dt><dd>${notable.map((c) => `<button type="button" class="atlas-link" data-id="${esc(c.i)}">${markSvg(c.k, 12)} ${esc(c.n)}</button>`).join('')}</dd>` : ''}
        ${strengths.length ? `<dt>${esc(t('region.strengths'))}</dt><dd>${strengths.map((x) => `<i>${esc(x)}</i>`).join('')}</dd>` : ''}
        ${gaps.length || sacGaps.length ? `<dt>${esc(t('region.gaps'))}</dt><dd>${[...sacGaps, ...gaps.map((x) => x[0].toUpperCase() + x.slice(1))].map((x) => `<i class="is-gap">${esc(x)}</i>`).join('')}</dd>` : ''}
      </dl>
      <button type="button" class="atlas-btn" data-fit>${esc(t('region.fit'))}</button>
    </section>`;
  }
  els.context.addEventListener('click', (e) => { const b = e.target.closest('.atlas-place [data-id]'); if (b) ctx.onSelect(b.dataset.id, 'place'); });

  /* ── search box ─────────────────────────────────────────────────────── */
  let groups = [], cursor = -1;
  const flat = () => groups.flatMap((g) => g.items);
  function showResults(gs) {
    groups = gs; cursor = -1;
    els.results.hidden = !gs.length;
    let i = 0;
    els.results.innerHTML = gs.map((g) => `<div class="atlas-rgroup"><h3>${esc(t('group.' + g.type))}</h3>${g.items.map((it) => {
      const idx = i++;
      const label = it.type === 'church' ? `${markSvg(it.church.k, 12)} <span>${esc(it.label)}</span><small>${esc(it.sub)}</small>`
        : it.type === 'place' ? `<span>${esc(it.label)}</span><small>${esc(t('results.count', { n: it.count }))}</small>`
        : `<span>${esc(it.label)}</span>`;
      return `<button type="button" class="atlas-result" role="option" data-idx="${idx}">${label}</button>`;
    }).join('')}</div>`).join('');
  }
  function moveCursor(d) {
    const items = els.results.querySelectorAll('.atlas-result');
    if (!items.length) return;
    cursor = (cursor + d + items.length) % items.length;
    items.forEach((el, i) => el.classList.toggle('is-active', i === cursor));
    items[cursor].scrollIntoView({ block: 'nearest' });
  }
  function pick(idx) {
    const it = flat()[idx]; if (!it) return;
    els.results.hidden = true; groups = [];
    ctx.onPick(it);
  }
  els.q.addEventListener('input', () => {
    const v = els.q.value;
    showResults(v.trim().length >= 2 ? ctx.search(v) : []);
    ctx.onQuery(v);
  });
  els.q.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); moveCursor(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveCursor(-1); }
    else if (e.key === 'Enter') { if (cursor >= 0) { e.preventDefault(); pick(cursor); } else if (flat().length === 1) { e.preventDefault(); pick(0); } else { els.results.hidden = true; } }
    else if (e.key === 'Escape') { if (!els.results.hidden) { els.results.hidden = true; e.stopPropagation(); } else if (els.q.value) { els.q.value = ''; ctx.onQuery(''); e.stopPropagation(); } }
  });
  els.results.addEventListener('mousedown', (e) => { const b = e.target.closest('[data-idx]'); if (b) { e.preventDefault(); pick(Number(b.dataset.idx)); } });
  document.addEventListener('click', (e) => { if (!e.target.closest('.atlas-search')) els.results.hidden = true; });

  return {
    els, renderChips, renderFacets, renderList, renderContext, highlight,
    resetPaging: () => { shown = PAGE; },
    setQuery: (v) => { if (els.q.value !== v) els.q.value = v; },
    focusSearch: () => { els.q.focus(); els.q.select(); },
  };
}

export { kindWord };
